/*
 * UploadReceiptModal
 *
 * A modal that used for handling the upload receipt (for users who are transacting using EDC)
 */

import { faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import Alert from "src/components-v2/Alert";
import Button from "src/components-v2/Button";
import FileInput from "src/components-v2/FileInput";
import Modal from "src/components-v2/Modal";
import PreviewPhoto from "src/components/PreviewPhoto";

import { mapErrorCode } from "src/helpers/api";
import { isFileTypePDF } from "src/helpers/file";
import { previewImage } from "src/helpers/object";

export type UploadReceiptModalProps = {
  /**
   * Current transaction id
   */
  transactionId?: string | number;

  /**
   * State to show and hide the modal
   */
  show?: boolean;

  /**
   * Page title (string) that will be used in the info panel
   * eg: "Riwayat Top-Up"
   */
  checkingPage?: string;

  /**
   * Modal toggle function
   */
  toggle?: Function;

  /**
   * Endpoint from the API interface that will be used to send the submitted file
   */
  uploadEndpoint?: Function;

  /**
   * Callback when the submission is running
   */
  onSubmitting?: Function;

  /**
   * Callback if the submission is succeed
   */
  onSuccess?: Function;

  /**
   * Callback if the submission is failed
   */
  onFailed?: Function;
};

const defaultProps = {
  transactionId: "",
  show: false,
  checkingPage: "",
  toggle: () => {},
  uploadEndpoint: () => {},
  onSubmitting: () => {},
  onSuccess: () => {},
  onFailed: () => {}
};

const MAX_FILE_SIZE_LIMIT = 10485760; // 10 MB
const ALLOWED_FILE_TYPES = ".pdf, .png, .jpg, .jpeg";

function UploadReceiptModal(props: UploadReceiptModalProps) {
  let isMounted = true;
  const { transactionId, show, toggle, checkingPage, uploadEndpoint, onSubmitting, onSuccess, onFailed } = props;
  const { t } = useTranslation(["translation", "errors"]);

  const [apiErrors, setApiErrors] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [previewSrc, setPreviewSrc] = useState(null);
  const [isPdf, setIsPdf] = useState(false);

  function setFile(file: File) {
    setUploadedFile(file);
    setIsPdf(isFileTypePDF(file.name));
    previewImage(file, setPreviewSrc);
  }

  function removeFile() {
    setUploadedFile(null);
    setPreviewSrc(null);
  }

  async function uploadImage() {
    if (!uploadedFile) return;

    setApiErrors([]);
    setIsSubmitting(true);
    onSubmitting();

    try {
      const formData = new FormData();
      formData.append("file", uploadedFile);
      formData.append("id", String(transactionId));

      const response = await uploadEndpoint(formData);
      if (response && isMounted) {
        setIsSubmitting(false);
        onSuccess();
      }
    } catch (errors) {
      const formattedErrors = mapErrorCode(t, errors);
      setApiErrors(formattedErrors);
      setIsSubmitting(false);
      onFailed();
    }
  }

  useEffect(() => {
    if (show) {
      removeFile();
    }

    return () => {
      isMounted = false;
    };
  }, [show]);

  return (
    <Modal
      title={t("upload-receipt-modal.title")}
      show={show}
      onClose={() => toggle(false)}
      closeOnOverlay
      withoutCloseButton
    >
      <p className="u-mb-6">{t("upload-receipt-modal.upload-info")}</p>

      {apiErrors.length > 0 && (
        <Alert icon={faExclamationTriangle} className="u-text-left u-mb-4">
          {apiErrors[0]}
        </Alert>
      )}

      {previewSrc ? (
        <PreviewPhoto
          fileName={uploadedFile.name}
          previewSrc={previewSrc}
          removeFile={removeFile}
          isPdf={isPdf}
          disabled={isSubmitting}
        />
      ) : (
        <FileInput
          placeholder={t("upload-receipt-modal.placeholder")}
          onChange={setFile}
          multiple={false}
          isUploading={isSubmitting}
          maxFileSize={MAX_FILE_SIZE_LIMIT}
          fileTypes={ALLOWED_FILE_TYPES}
          resetFile={show}
          disabled={isSubmitting}
        />
      )}

      <Alert color="yellow" className="u-text-left u-my-6">
        {t("upload-receipt-modal.checking-info")}
        <b>{checkingPage}</b>.
      </Alert>

      <div className="u-text-right">
        <Button activated={isSubmitting} disabled={isSubmitting || !uploadedFile} onClick={uploadImage}>
          {t("upload-receipt-modal.submit")}
        </Button>
      </div>
    </Modal>
  );
}

UploadReceiptModal.defaultProps = defaultProps;

export default UploadReceiptModal;
